// The bundled home and website deliberately share this small storage protocol.
// Only explicit downloads enter it. No HTTP responses or authentication tokens do.
export const LIBRARY_DB = 'pw-offline-home';
export const MAX_ITEM_BYTES = 4 * 1024 * 1024;
export const MAX_LIBRARY_BYTES = 24 * 1024 * 1024;
export const MAX_LIBRARY_ITEMS = 20;

export function nativePlugin() {
  const capacitor = globalThis.window?.Capacitor;
  if (!capacitor?.isNativePlatform?.() || !capacitor.isPluginAvailable?.('PeakWaveOffline')) return null;
  return capacitor.Plugins?.PeakWaveOffline ?? capacitor.registerPlugin?.('PeakWaveOffline') ?? null;
}


export function validKey(key) { return typeof key === 'string' && /^(map|journey|window|outing|weather):[a-zA-Z0-9:_-]{1,160}$/.test(key); }
export function validOwner(owner) { return typeof owner === 'string' && /^[a-zA-Z0-9_-]{1,80}$/.test(owner); }
export async function checksum(raw) {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  return [...new Uint8Array(hash)].map(value => value.toString(16).padStart(2, '0')).join('');
}

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(LIBRARY_DB, 1);
    let blocked = false;
    request.onupgradeneeded = () => {
      request.result.createObjectStore('context');
      request.result.createObjectStore('items', { keyPath: 'key' });
    };
    request.onsuccess = () => {
      if (blocked) { request.result.close(); return; }
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
    request.onerror = () => reject(request.error);
    request.onblocked = () => { blocked = true; reject(new Error('Close other PeakWave tabs and retry.')); };
  });
}

async function transaction(mode, action) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(['context', 'items'], mode);
      let value, failure;
      tx.oncomplete = () => resolve(value);
      tx.onerror = () => reject(failure ?? tx.error);
      tx.onabort = () => reject(failure ?? tx.error);
      const fail = error => { failure = error; tx.abort(); };
      const context = tx.objectStore('context').get('current');
      context.onsuccess = () => {
        try { action(tx, context.result ?? { owner: 'device', generation: 'initial' }, result => { value = result; }, fail); }
        catch (error) { fail(error); }
      };
    });
  } finally { db.close(); }
}

export async function getLibraryContext() {
  if (nativePlugin()) return nativePlugin().getContext();
  return transaction('readonly', (_tx, context, done) => done(context));
}

export async function setLibraryOwner(owner) {
  if (!validOwner(owner)) throw new Error('Account details are unavailable.');
  if (nativePlugin()) return nativePlugin().setOwner({ owner });
  const result = await transaction('readwrite', (tx, current, done) => {
    const next = current.owner === owner ? current : { owner, generation: crypto.randomUUID() };
    if (current.owner !== owner) tx.objectStore('items').clear();
    tx.objectStore('context').put(next, 'current');
    done(next);
  });
  // Signal other open web homes without copying account details into localStorage.
  try { localStorage.setItem('peakwave-offline-generation', result.generation); } catch { /* IDB remains authoritative. */ }
  return result;
}

function matches(current, expected) {
  if (!expected || current.owner !== expected.owner || current.generation !== expected.generation) {
    throw new Error('The account changed. Reopen your downloads and try again.');
  }
}

export async function putLibraryItem(key, raw, expected) {
  const bytes = typeof raw === 'string' ? new TextEncoder().encode(raw).length : 0;
  if (!validKey(key) || !bytes || bytes > MAX_ITEM_BYTES) throw new Error('This download is incomplete or exceeds the 4 MB limit.');
  if (nativePlugin()) return nativePlugin().put({ key, raw, ...expected });
  const hash = await checksum(raw);
  return transaction('readwrite', (tx, current, done, fail) => {
    matches(current, expected);
    const store = tx.objectStore('items');
    const request = store.getAll();
    request.onsuccess = () => {
      try {
        const others = request.result.filter(item => item.key !== key);
        if (others.length >= MAX_LIBRARY_ITEMS || (key.startsWith('journey:') && others.filter(item => item.key.startsWith('journey:')).length >= 8) || others.reduce((sum, item) => sum + item.bytes, bytes) > MAX_LIBRARY_BYTES) {
          fail(new Error('Offline storage is full. Remove an old download and retry. Your previous copy is still available.'));
          return;
        }
        store.put({ key, raw, bytes, hash });
        done(undefined);
      } catch (error) {
        fail(error?.name === 'QuotaExceededError' ? new Error('Device storage is full. Your previous copy is kept. Remove an old download and retry.') : error);
      }
    };
  });
}

export async function listLibraryKeys(expected) {
  if (nativePlugin()) return (await nativePlugin().list({ ...expected })).keys;
  return transaction('readonly', (tx, current, done) => {
    matches(current, expected);
    const request = tx.objectStore('items').getAllKeys();
    request.onsuccess = () => done(request.result);
  });
}

export async function readLibraryItem(key, expected) {
  if (!validKey(key)) throw new Error('Unrecognised download.');
  if (nativePlugin()) return (await nativePlugin().read({ key, ...expected })).raw;
  const item = await transaction('readonly', (tx, current, done) => {
    matches(current, expected);
    const request = tx.objectStore('items').get(key);
    request.onsuccess = () => done(request.result ?? null);
  });
  if (!item) return null;
  if (typeof item.raw !== 'string' || new TextEncoder().encode(item.raw).length > MAX_ITEM_BYTES || await checksum(item.raw) !== item.hash) {
    throw new Error('This download is damaged. Remove it and download it again when connected.');
  }
  // Hashing is asynchronous; do not return an old account's data after a switch.
  matches(await getLibraryContext(), expected);
  return item.raw;
}

export async function removeLibraryItem(key, expected) {
  if (!validKey(key)) throw new Error('Unrecognised download.');
  if (nativePlugin()) return nativePlugin().remove({ key, ...expected });
  return transaction('readwrite', (tx, current) => { matches(current, expected); tx.objectStore('items').delete(key); });
}
